import { ReactNode, useEffect } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { useUIStore } from '@/store';
import { useAuth } from '@/auth';
import { setTokenGetter } from '@/api';
import { setApiTokenGetter } from '@/api/apiInstance';
import { setNdBearerTokenGetter } from '@/utils/ndTokenManager';
import { theme } from '@/theme';
import { useBreakpoint, useMediaQuery } from '@/hooks';
import { ToastContainer } from '@/components/ui';
import { useNdIframeIntegration } from '@/hooks/useNdIframeIntegration';
import { useQueryClient } from '@tanstack/react-query';
import { isExtensionHost } from '@/utils/extensionHost';
import { ExtensionAccountBar } from './ExtensionAccountBar';
import { PanelBrandingReporter } from './PanelBrandingReporter';

interface AppShellProps {
  children: ReactNode;
}

export const AppShell = ({ children }: AppShellProps) => {
  const { sidebarCollapsed } = useUIStore();
  const { getAccessToken } = useAuth();
  const { isMobile, isTablet } = useBreakpoint();

  // Small desktop: 1024-1280px - force sidebar to collapsed state
  const isSmallDesktop = useMediaQuery('(min-width: 1024px) and (max-width: 1279px)');

  // Configure API client with token getter
  useEffect(() => {
    setTokenGetter(getAccessToken);
    setApiTokenGetter(getAccessToken);
    setNdBearerTokenGetter(getAccessToken);

    // Now that the Bearer token is available, re-validate the ND token
    // (the initial bootstrap attempt may have failed without it).
    import('@/store/useNetDocumentsStore').then(({ useNetDocumentsStore }) => {
      useNetDocumentsStore.getState().validateToken();
    });
  }, [getAccessToken]);

  // NetDocuments iframe integration (handles from=nd URL params, postMessage relay, etc.)
  useNdIframeIntegration(true);

  // Listen for DocuSign callback completion from child tabs
  const queryClient = useQueryClient();
  useEffect(() => {
    const handleDocuSignComplete = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'motr:docusign-complete') {
        // Refresh all step-detail queries (documents, job, activity)
        queryClient.invalidateQueries({ queryKey: ['step-detail'] });
      }
    };
    window.addEventListener('message', handleDocuSignComplete);
    return () => window.removeEventListener('message', handleDocuSignComplete);
  }, [queryClient]);

  // Calculate left margin based on screen size and sidebar state
  // Mobile/Tablet: No margin (sidebar is overlay)
  // Small Desktop (1024-1279px): Always use collapsed margin for more content space
  // Large Desktop: Margin based on collapsed/expanded state
  const getMainMarginLeft = () => {
    if (isMobile || isTablet) {
      return '0px';
    }
    // On small desktop, always use collapsed width regardless of sidebar state
    if (isSmallDesktop) {
      return '60px';
    }
    return sidebarCollapsed ? '60px' : '260px';
  };

  // Chrome extension side panel: the page alone, no navigation — the panel only starts jobs.
  if (isExtensionHost()) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: theme.colors.backgroundPage }}>
        <main style={{ flex: 1, overflow: 'auto', padding: theme.spacing.md }}>{children}</main>
        <ExtensionAccountBar />
        <PanelBrandingReporter />
        <ToastContainer />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        backgroundColor: theme.colors.backgroundPage,
      }}
    >
      <Sidebar />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: getMainMarginLeft(),
          transition: 'margin-left 0.2s ease',
          minWidth: 0,
        }}
      >
        <Header />
        <main
          style={{
            flex: 1,
            overflow: 'auto',
            padding: isMobile ? theme.spacing.md : `${theme.spacing.xl} 32px`,
          }}
        >
          {children}
        </main>
      </div>
      {/* Global toast notifications */}
      <ToastContainer />
    </div>
  );
};
